import Assessment from '@app/src/types/assessment'
import { comparePeriods } from '@app/src/utils/getOrderedPeriods'
import { useMemo } from 'react'

export enum TrendDirection {
  Up = 'up',
  Down = 'down',
  Same = 'same',
}

export interface AssessmentTrendData {
  hasTrend: boolean
  previousAssessment?: Assessment
  currentIndex: number
  providerAssessments: Assessment[]
  trendDirection?: TrendDirection
}

export const calculateAssessmentTrend = (assessment: Assessment, allAssessments: Assessment[]): AssessmentTrendData => {
  const providerAssessments = allAssessments
    .filter(
      a =>
        a.providerId === assessment.providerId &&
        a.assessmentTemplateId === assessment.assessmentTemplateId &&
        a.totalLevel !== undefined &&
        a.totalLevel !== null,
    )
    .sort((a, b) => {
      return comparePeriods(a.periodName, b.periodName)
    })

  if (providerAssessments.length < 2) {
    return {
      hasTrend: false,
      currentIndex: -1,
      providerAssessments,
    }
  }

  const currentIndex = providerAssessments.findIndex(a => a.id === assessment.id)

  if (currentIndex <= 0) {
    return {
      hasTrend: false,
      currentIndex,
      providerAssessments,
    }
  }

  const previousAssessment = providerAssessments[currentIndex - 1]
  const currentLevel = assessment.totalLevel ?? 0
  const previousLevel = previousAssessment.totalLevel ?? 0

  let trendDirection: TrendDirection = TrendDirection.Same
  if (currentLevel > previousLevel) {
    trendDirection = TrendDirection.Up
  } else if (currentLevel < previousLevel) {
    trendDirection = TrendDirection.Down
  }

  return {
    hasTrend: true,
    previousAssessment,
    currentIndex,
    providerAssessments,
    trendDirection,
  }
}

export const useAssessmentTrends = (assessments: Assessment[], allAssessments: Assessment[]) => {
  return useMemo(() => {
    if (!assessments?.length || !allAssessments?.length) {
      return {
        hasAnyTrends: false,
        trendData: [],
        getTrendForAssessment: () => ({ hasTrend: false, currentIndex: -1, providerAssessments: [] }),
      }
    }

    const trendData = assessments.map(assessment => calculateAssessmentTrend(assessment, allAssessments))

    const hasAnyTrends = trendData.some(trend => trend.hasTrend)

    const getTrendForAssessment = (assessment: Assessment): AssessmentTrendData => {
      return calculateAssessmentTrend(assessment, allAssessments)
    }

    return {
      hasAnyTrends,
      trendData,
      getTrendForAssessment,
    }
  }, [assessments, allAssessments])
}
